import React, { useState, useRef, useEffect } from "react";
import { LuX, LuChevronDown, LuCheck } from "react-icons/lu";

export type Option = {
    label: string;
    value: string;
};

type Props = {
    options: Option[];
    value?: string[];
    onChange: (value: string[]) => void;
    label: string;
    error?: string;
    placeholder?: string;
    maxVisibleBadges?: number;
};

export const MultiSelect: React.FC<Props> = ({
    options,
    value = [],
    onChange,
    label,
    error,
    placeholder = "Select...",
    maxVisibleBadges,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const visibleValues =
        typeof maxVisibleBadges === "number" ? value.slice(0, maxVisibleBadges) : value;
    const remainingCount = value.length - visibleValues.length;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (optionValue: string) => {
        if (value.includes(optionValue)) {
            onChange(value.filter((v) => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    const removeValue = (e: React.MouseEvent, optionValue: string) => {
        e.stopPropagation();
        onChange(value.filter((v) => v !== optionValue));
    };

    return (
        <div className="w-full" ref={containerRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="relative">
                <div
                    className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 min-h-[42px] cursor-pointer flex flex-wrap gap-2 items-center ${error ? "border-red-500" : ""
                        }`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {value.length === 0 && <span className="text-gray-400">{placeholder}</span>}
                    {visibleValues.map((val) => {
                        const option = options.find((o) => o.value === val);
                        return (
                            <span
                                key={val}
                                className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center gap-1"
                            >
                                {option?.label || val}
                                <LuX
                                    size={14}
                                    className="cursor-pointer hover:text-blue-900"
                                    onClick={(e) => removeValue(e, val)}
                                />
                            </span>
                        );
                    })}
                    {remainingCount > 0 && (
                        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded">
                            +{remainingCount} more
                        </span>
                    )}
                    <div className="ml-auto">
                        <LuChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                </div>

                {isOpen && (
                    <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                        {options.map((option) => {
                            const isSelected = value.includes(option.value);
                            return (
                                <div
                                    key={option.value}
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${isSelected ? "bg-blue-50 text-blue-600" : "text-gray-700"
                                        }`}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    <span>{option.label}</span>
                                    {isSelected && <LuCheck size={16} />}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};
